import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SpokeIcon from "@mui/icons-material/Spoke";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";

import CommonLayout from "../Common/Layout";
import MultiSelectDropDown from "../Common/MultiSelectDropDown";
import ViewProfile from "./ViewProfile";
import KMKTable from "../Common/Table/KMKTable";

import { DATA_SOURCE } from "../../util/Data/DataModeling";
import { convertTimezone, setPrevState } from "../../util/common";

import DataCatalogService from "../../service/datacatalogService";
import { TAG_COLUMN_PREFIX } from "./DataCatalogData";
import MultiSelect from "../../util/multiselect";
import useWindowResize from "../../hooks/useWindowsReszie";
import Toolbar from "../Common/Toolbar";

const rowsPerPageOptions = [10, 25, 50, 100];

const useStyles = makeStyles()((theme) => ({
  PanelContainer: {
    padding: "10px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  pagination: {
    "& p": {
      marginBottom: "0px",
    },
  },
  ImgIcon: {
    height: "1.2rem",
    width: "1.2rem",
    marginRight: "5px",
  },
  NameCell: {
    display: "flex",
    alignContent: "center",
    alignItems: "center",
  },
}));

function DataCatalog() {
  const [loading, setLoading] = useState(false);

  const [loaded, setLoaded] = useState(false);
  const [count, setCount] = useState(0);
  const [options, setOptions] = useState({
    limit: 10,
    page: 0,
    orderBy: "Name",
    order: "asc",
    search: "",
    tags: [],
  });
  const [data, setData] = useState([]);
  const [viewProfile, setViewProfile] = useState({
    visible: false,
    selectedDC: null,
  });
  const [tags, setTags] = useState([]);

  const { classes } = useStyles();
  const { height: tableHeight } = useWindowResize(260);
  const listingRef = useRef(options);

  const user = useSelector((state) => state.user);
  const { UserTimezone } = useMemo(() => user?.currentUser || {}, [user]);

  //Loading account List
  const loadCatalogdata = useCallback(
    async (options) => {
      setLoading(true);

      const dcService = new DataCatalogService();
      const { success, data } = await dcService.getAllDCs({
        search: listingRef.current.search,
        limit: listingRef.current.limit,
        page: listingRef.current.page,
        orderBy: listingRef.current.orderBy,
        order: listingRef.current.order,
        tags: listingRef.current.tags,
        tz: UserTimezone,
        ...options,
      });

      if (success && data && Object.keys(data.catalogList).length > 0) {
        setData(data.catalogList);
        setCount(data.count);
        setLoaded(true);
      } else {
        setData([]);
        setCount(0);
      }

      setLoading(false);
    },
    [UserTimezone]
  );

  const updateListingRefObj = useCallback(
    (passedOptions) => {
      listingRef.current = { ...options, ...passedOptions };
      loadCatalogdata(passedOptions);
    },
    [loadCatalogdata, options]
  );

  const setOptionsHelper = useCallback((properties) => {
    setPrevState(setOptions, properties);
  }, []);

  const handleChangePage = useCallback(
    (_event, newPage) => {
      setOptionsHelper({ page: newPage });
      updateListingRefObj({
        page: newPage,
      });
    },
    [setOptionsHelper, updateListingRefObj]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      setOptionsHelper({ limit: parseInt(event.target.value, 10), page: 0 });
      updateListingRefObj({
        limit: parseInt(event.target.value, 10),
        page: 0,
      });
    },
    [setOptionsHelper, updateListingRefObj]
  );

  const handleRequestSort = useCallback(
    (property) => {
      const isAsc = options.orderBy === property && options.order === "asc";
      let tmpOrder = isAsc ? "desc" : "asc";

      setOptionsHelper({ order: tmpOrder, orderBy: property });
      updateListingRefObj({ orderBy: property, order: tmpOrder });
    },
    [options.order, options.orderBy, setOptionsHelper, updateListingRefObj]
  );

  const handleClearSearch = useCallback(() => {
    setOptionsHelper({ page: 0, search: "" });
    updateListingRefObj({
      search: "",
      page: 0,
    });
  }, [setOptionsHelper, updateListingRefObj]);

  const handleSearch = useCallback(
    (e) => {
      if (e.key === "Enter") {
        setOptionsHelper({ page: 0 });
        updateListingRefObj({ page: 0 });
      }
    },
    [setOptionsHelper, updateListingRefObj]
  );

  const handleTagsChange = useCallback(
    (selectionList) => {
      if (
        !options.orderBy.startsWith(TAG_COLUMN_PREFIX) ||
        (options.orderBy.startsWith(TAG_COLUMN_PREFIX) &&
          selectionList.includes(options.orderBy.slice(TAG_COLUMN_PREFIX.length)))
      ) {
        setOptionsHelper({ tags: selectionList });
        updateListingRefObj({ tags: selectionList });
      } else {
        setOptionsHelper({ tags: selectionList, orderBy: "Name", order: "asc" });
        updateListingRefObj({ tags: selectionList, orderBy: "Name", order: "asc" });
      }
    },
    [options.orderBy, setOptionsHelper, updateListingRefObj]
  );

  const handleViewProfile = useCallback((catalog = null) => {
    setPrevState(setViewProfile, { visible: catalog !== null, selectedDC: catalog });
  }, []);

  useEffect(() => {
    const getTags = async () => {
      const dcService = new DataCatalogService();
      const { data, success } = await dcService.getTags();
      if (success) {
        setTags(data.map(({ TagKey }) => ({ Name: TagKey })));
        const selectedTags = data.filter(({ isSelected }) => isSelected).map(({ TagKey }) => TagKey);
        listingRef.current.tags = selectedTags;
        setOptions((prev) => ({ ...prev, tags: selectedTags }));
      }
    };

    (async () => {
      setLoading(true);
      await getTags();
      await loadCatalogdata();
      setLoading(false);
    })();

    return () => {
      setData([]);
      setTags([]);
    };
  }, [loadCatalogdata]);

  const columns = useMemo(() => {
    return loaded
      ? [
          {
            id: "Name",
            name: "Name",
            sortable: true,
            align: "left",
            width: 150,
            renderHeadCell: (name) => name,
            renderValueCell: (val, record, index) => (
              <span className={classes.NameCell}>
                {record.TypeId === 1 ? (
                  <img src="/images/database.png" alt={DATA_SOURCE.singular} className={classes.ImgIcon} />
                ) : (
                  <></>
                )}
                {val || "-"}
              </span>
            ),
          },
          {
            id: "Description",
            name: "Description",
            sortable: false,
            align: "left",
            width: 200,
            renderHeadCell: (name) => name,
            renderValueCell: (val, record, index) => val,
            overflowTooltip: true,
          },
          {
            id: "Type",
            name: "Type",
            sortable: true,
            align: "left",
            width: 150,
            renderHeadCell: (name) => name,
            renderValueCell: (val, record, index) => val || "-",
          },
          {
            id: "ContainedIn",
            name: "Contained In",
            sortable: true,
            align: "left",
            width: 200,
            renderHeadCell: (name) => name,
            renderValueCell: (val, record, index) => (
              <span className={classes.NameCell}>
                {record.SourceType === 1 ? <SpokeIcon sx={{ fontSize: "1.2rem", mr: "5px" }} /> : <></>}
                {val || ""}
              </span>
            ),
          },
          {
            id: "Updated",
            name: "Last Updated",
            sortable: true,
            align: "left",
            width: 150,
            renderHeadCell: (name) => name,
            renderValueCell: (val, record, index) => (val ? convertTimezone(val) : "-"),
          },
          ...options.tags.map((tag) => ({
            id: `${TAG_COLUMN_PREFIX}${tag}`,
            name: tag,
            sortable: true,
            align: "left",
            width: 200,
            renderHeadCell: (name) => (
              <span className={classes.NameCell}>
                {name}
                {<LocalOfferOutlinedIcon sx={{ fontSize: "1.2rem", ml: "5px" }} />}
              </span>
            ),
            renderValueCell: (val, record, index) => val || "-",
            overflowTooltip: true,
          })),
          {
            id: "Action",
            name: "Action",
            sortable: false,
            align: "center",
            width: 120,
            renderHeadCell: (name) => name,
            renderValueCell: (val, record, index) => (
              <Tooltip
                title={<Typography variant="body2">View Profile</Typography>}
                style={{ verticalAlign: "middle" }}
                arrow
              >
                <IconButton color="primary" onClick={() => handleViewProfile(record)}>
                  <FindInPageIcon sx={{ fontSize: "1.4rem" }} />
                </IconButton>
              </Tooltip>
            ),
          },
        ]
      : [];
  }, [classes.ImgIcon, classes.NameCell, handleViewProfile, loaded, options.tags]);

  const breadcrumbArr = useMemo(() => [{ link: "#", displayName: "Data Catalog", active: true }], []);

  return (
    <CommonLayout title="Data Catalog" breadcrumbArr={breadcrumbArr} loading={loading}>
      <Container maxWidth="false" className={classes.PanelContainer}>
        <Toolbar title="Data Catalog">
          <Box sx={{ display: "flex", flexWrap: "nowrap", flex: "1", flexDirection: "row-reverse" }}>
            <TextField
              data-id="DCSearch"
              value={options.search}
              onKeyDown={handleSearch}
              onChange={(e) => setOptionsHelper({ search: e.target.value })}
              placeholder="Search"
              variant="standard"
              InputProps={{
                startAdornment: <SearchIcon color="primary" />,
                ...(options.search && {
                  endAdornment: (
                    <IconButton onClick={handleClearSearch} size="small">
                      <ClearIcon color="primary" />
                    </IconButton>
                  ),
                }),
              }}
              sx={{ width: "180px", minWidth: "180px" }}
              disabled={loading}
            />
            <MultiSelectDropDown
              dataid="id"
              variant="standard"
              placeHolder={<MultiSelect title="Tags" dataLength={options.tags.length} />}
              options={tags}
              selected={options.tags}
              isSelectAll={false}
              disabled={loading}
              emptySelectionAllowed={true}
              onSelectChange={handleTagsChange}
              basedOn="Name"
              allowSearching
            />
          </Box>
        </Toolbar>
        <Paper sx={{ width: "100%", mb: 2 }}>
          <KMKTable
            loading={loading}
            columns={columns}
            containerHeight={tableHeight}
            noDataText={"No records found"}
            data={data}
            sortingProperties={{ order: options.order, orderBy: options.orderBy, handleSorting: handleRequestSort }}
            boldHeader
            tableProps={{ size: "small", stickyHeader: true }}
            pagination={true}
            paginationProperties={{
              rowsPerPageOptions,
              count,
              rowsPerPage: options.limit,
              page: options.page,
              onPageChange: handleChangePage,
              handleChangeRowsPerPage: handleChangeRowsPerPage,
              component: "div",
              className: classes.pagination,
              "data-id": "commonListPagination",
            }}
            dataIdKey="DataCatalog_id"
          />
        </Paper>
      </Container>
      {viewProfile.visible && (
        <ViewProfile
          visible={viewProfile.visible}
          selectedDC={viewProfile.selectedDC}
          handleClose={() => handleViewProfile()}
        />
      )}
    </CommonLayout>
  );
}

export default DataCatalog;
